import { Fragment } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  Home, 
  ChevronRight, 
  GraduationCap, 
  LayoutDashboard,
  BookOpen,
  FileText,
  ClipboardCheck,
  Shield,
  DollarSign
} from "lucide-react";

interface BreadcrumbItem {
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  path?: string;
  active?: boolean;
}

interface UniversalBreadcrumbProps {
  items?: BreadcrumbItem[];
  className?: string;
}

export function UniversalBreadcrumb({ items, className = "" }: UniversalBreadcrumbProps) {
  const navigate = useNavigate();
  const location = useLocation();

  // Auto-generate breadcrumbs based on current path if not provided
  const breadcrumbItems: BreadcrumbItem[] = items || generateBreadcrumbs(location.pathname);

  const handleNavigate = (path?: string) => {
    if (path) {
      navigate(path);
    }
  };

  return (
    <nav 
      aria-label="Breadcrumb" 
      className={`flex items-center gap-2 text-sm ${className}`}
    >
      <ol className="flex items-center gap-2 flex-wrap">
        {breadcrumbItems.map((item, index) => {
          const Icon = item.icon || Home;
          const isLast = index === breadcrumbItems.length - 1;
          
          return (
            <Fragment key={index}>
              <li className="flex items-center gap-2">
                {item.path && !isLast ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleNavigate(item.path)}
                    className="h-auto p-2 hover:bg-muted transition-medical"
                  >
                    <Icon className="h-3 w-3 mr-1.5" />
                    <span className={item.active ? "font-medium text-foreground" : "text-muted-foreground"}>
                      {item.label}
                    </span>
                  </Button>
                ) : (
                  <div className="flex items-center gap-1.5 px-2 py-1">
                    <Icon className="h-3 w-3" />
                    <span className={`${isLast ? "font-medium text-foreground" : "text-muted-foreground"}`}>
                      {item.label}
                    </span>
                  </div>
                )}
              </li>
              {!isLast && (
                <li>
                  <ChevronRight className="h-3 w-3 text-muted-foreground" />
                </li>
              )}
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}

// Helper function to auto-generate breadcrumbs from path
function generateBreadcrumbs(pathname: string): BreadcrumbItem[] {
  const paths = pathname.split('/').filter(Boolean);
  const breadcrumbs: BreadcrumbItem[] = [
    { label: "Home", icon: Home, path: "/" }
  ];

  const routeMap: Record<string, { label: string; icon: React.ComponentType<{ className?: string }> }> = {
    'study': { label: 'Study Hub', icon: GraduationCap },
    'dashboard': { label: 'Dashboard', icon: LayoutDashboard },
    'practice': { label: 'Practice', icon: BookOpen },
    'notes': { label: 'Notes', icon: FileText },
    'mock': { label: 'Mock Exams', icon: ClipboardCheck },
    'admin': { label: 'Admin', icon: Shield },
    'pricing': { label: 'Pricing', icon: DollarSign },
  };

  let currentPath = '';
  paths.forEach((segment, index) => {
    currentPath += `/${segment}`;
    const routeInfo = routeMap[segment];
    
    if (routeInfo) {
      breadcrumbs.push({
        label: routeInfo.label,
        icon: routeInfo.icon,
        path: currentPath,
        active: index === paths.length - 1
      });
    } else {
      // For dynamic segments (like topic IDs), show capitalized version
      breadcrumbs.push({
        label: segment.charAt(0).toUpperCase() + segment.slice(1),
        path: currentPath,
        active: index === paths.length - 1
      });
    }
  });

  return breadcrumbs;
}
